// ─── Declarative widget renderer (PLATFORM_SDK §2.2 mode B — the XSS boundary) ───────────
//
// The host-side renderer for the FIXED widget vocabulary. This is where an external plugin's
// declarative `WidgetSchema` becomes UI. It NEVER takes raw HTML/JS: every text node is a plain
// string (React escapes it), and `markdown` goes through the same sanitized markdown path the
// chat uses (renderContent → sanitizeHtml). A plugin can only say "show a card/chips/button/
// progress/markdown" — it cannot inject markup or run code on the main thread.

import type { ReactNode } from 'react'
import { renderContent } from '../plugins/renderContent'
import type { SlotContext, SlotContribution, WidgetSchema, WidgetAction } from './types'

/** Run a widget's declared action through the host's safe ops — never arbitrary code. */
function runAction(action: WidgetAction | undefined, ctx: SlotContext): void {
  if (!action) return
  if ('submit' in action) ctx.submit(action.submit)
  else if ('input' in action) ctx.sendInput(action.input)
}

export function WidgetView({ schema, ctx }: { schema: WidgetSchema; ctx: SlotContext }) {
  switch (schema.kind) {
    case 'card':
      return (
        <div className="slot-widget slot-widget--card">
          {schema.title && <div className="slot-widget-title">{schema.title}</div>}
          {schema.body && <div className="slot-widget-body">{schema.body}</div>}
        </div>
      )

    case 'chips':
      return (
        <div className="slot-widget slot-widget--chips">
          {schema.items.map((t, i) => (
            <span className="slot-widget-chip" key={i}>
              {t}
            </span>
          ))}
        </div>
      )

    case 'button':
      return (
        <button
          type="button"
          className="slot-widget slot-widget--button"
          onClick={() => runAction(schema.action, ctx)}
        >
          {schema.label}
        </button>
      )

    case 'progress': {
      const max = typeof schema.max === 'number' && schema.max > 0 ? schema.max : 100
      const pct = Math.max(0, Math.min(100, (schema.value / max) * 100))
      return (
        <div className="slot-widget slot-widget--progress">
          {schema.label && <span className="slot-widget-progress-label">{schema.label}</span>}
          <div className="slot-widget-progress-track">
            <div className="slot-widget-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )
    }

    case 'markdown':
      // Reuse the chat's sanitized markdown renderer — no separate sanitizer to keep in sync.
      return <div className="slot-widget slot-widget--markdown">{renderContent(schema.text)}</div>

    case 'embed': {
      // A sandboxed iframe app (e.g. a drawing canvas served by a kind:service plugin). The manifest
      // supplies a URL, never markup; we only ever embed http(s). `{chatId}` is substituted so the
      // embedded app scopes to the active chat (loads/saves that chat's state). sandbox keeps it
      // isolated; allow-same-origin lets the (cross-origin) app use its own origin for WS/fetch.
      // Allow a same-origin root-relative path ("/canvas/…", preferred — survives remote domains)
      // or an explicit http(s) URL. Reject protocol-relative ("//host") and anything else.
      const raw = schema.src ?? ''
      if (!(/^https?:\/\//i.test(raw) || /^\/(?!\/)/.test(raw))) return null
      const src = raw.replace(/\{chatId\}/g, encodeURIComponent(ctx.chatId))
      return (
        <iframe
          className="slot-widget slot-widget--embed"
          src={src}
          title={schema.title ?? 'Embedded app'}
          style={{ width: '100%', height: schema.height ? `${schema.height}px` : '100%', border: 'none' }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-pointer-lock allow-downloads"
          allow="clipboard-read; clipboard-write"
        />
      )
    }

    default:
      return null
  }
}

/** Render ONE contribution to a node — internal `render(ctx)` (mode A) or the declarative widget
 *  (mode B). Shared by SlotHost and any position that mounts slot items into its own layout
 *  (e.g. composer-above chips). Returns null when a contribution supplies neither. */
export function renderSlotNode(c: SlotContribution, ctx: SlotContext): ReactNode {
  if (c.render) return c.render(ctx)
  if (c.widget) return <WidgetView schema={c.widget} ctx={ctx} />
  return null
}
